<template>
  <div class="platform-list-page">
    <Loader v-if="loaderEnabled" v-bind:logo="loaderLogo"></Loader>

    <b-row>
      <b-col md="12" lg="12" sm="12">
        <KTCodePreview>
          <template v-slot:preview>
            <div class="platform-list-page__header">
              <div
                class="d-flex justify-content-between align-items-center mb-4"
              >
                <h5 class="mb-0">Danh sách kết nối sàn thương mại điện tử</h5>
                <b-button
                  variant="primary"
                  size="sm"
                  @click="navigateToConnect"
                >
                  <i class="flaticon2-plus"></i> Kết nối mới
                </b-button>
              </div>
            </div>

            <div v-if="error" class="alert alert-danger" role="alert">
              <div class="d-flex justify-content-between align-items-center">
                <div>{{ error }}</div>
                <b-button variant="danger" size="sm" @click="loadConnections">
                  <i class="flaticon2-refresh"></i> Thử lại
                </b-button>
              </div>
            </div>

            <div
              v-if="!loading && connections.length === 0"
              class="platform-list-page__empty"
            >
              <div class="text-center py-5">
                <i class="flaticon2-layers-1 icon-3x text-muted mb-3"></i>
                <h4 class="text-muted mb-3">
                  Chưa có kết nối sàn thương mại điện tử nào
                </h4>
                <p class="text-muted mb-4">
                  Kết nối với Shopee, TikTok Shop để đồng bộ sản phẩm và đơn
                  hàng
                </p>
                <b-button variant="primary" @click="navigateToConnect">
                  <i class="flaticon2-plus"></i> Kết nối ngay
                </b-button>
              </div>
            </div>

            <b-table
              v-if="!loading && connections.length > 0"
              :items="connections"
              :fields="tableFields"
              :busy="loading"
              striped
              hover
              responsive
              show-empty
              class="platform-list-page__table"
            >
              <template #table-busy>
                <div class="text-center text-primary my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong class="ml-2">Đang tải...</strong>
                </div>
              </template>

              <template #cell(platform)="data">
                <div class="d-flex align-items-center">
                  <img
                    :src="getPlatformLogo(data.item.platform)"
                    :alt="data.item.platform"
                    class="platform-list-page__logo mr-2"
                  />
                  <span class="font-weight-bold">{{
                    getPlatformName(data.item.platform)
                  }}</span>
                </div>
              </template>

              <template #cell(shopName)="data">
                <div>
                  <div class="font-weight-bold">{{ data.item.shopName }}</div>
                  <small class="text-muted">ID: {{ data.item.shopId }}</small>
                </div>
              </template>

              <template #cell(storeName)="data">
                {{ data.item.storeName || 'Chưa liên kết' }}
              </template>

              <template #cell(installmentAccount)="data">
                <div v-if="data.item.installmentAccount" class="installment-cell">
                  <div class="font-weight-bold text-truncate" style="max-width: 220px">
                    {{ data.item.installmentAccount.name || '—' }}
                  </div>
                  <small
                    v-if="data.item.installmentAccount.code"
                    class="text-muted d-block"
                    >{{ data.item.installmentAccount.code }}</small
                  >
                </div>
                <b-badge
                  v-else-if="data.item.installmentAccountId != null"
                  variant="warning"
                  pill
                >
                  TK không hợp lệ
                </b-badge>
                <span v-else class="text-muted">—</span>
              </template>

              <template #cell(status)="data">
                <b-badge :variant="getStatusVariant(data.item)">
                  {{ getStatusIcon(data.item) }}
                  {{ getStatusText(data.item) }}
                </b-badge>
                <div
                  v-if="
                    getEffectiveStatus(data.item) === 'error' &&
                      data.item.errorMessage
                  "
                  class="text-danger small mt-1"
                >
                  {{ data.item.errorMessage }}
                </div>
                <div
                  v-if="getEffectiveStatus(data.item) === 'expired'"
                  class="text-warning small mt-1"
                >
                  Token đã hết hạn, vui lòng kết nối lại
                </div>
              </template>

              <template #cell(createdAt)="data">
                {{ formatDate(data.item.createdAt) }}
              </template>

              <template #cell(actions)="data">
                <div class="platform-list-page__actions">
                  <b-dropdown
                    size="sm"
                    variant="link"
                    toggle-class="platform-list-page__actions-toggle"
                    no-caret
                    right
                    boundary="viewport"
                    :popper-opts="{ positionFixed: true }"
                    :aria-label="`Hành động cho ${data.item.shopName}`"
                  >
                    <template #button-content>
                      <i
                        class="flaticon2-settings platform-list-page__actions-icon"
                      ></i>
                    </template>
                    <b-dropdown-item
                      class="platform-list-page__actions-item"
                      @click="manageProducts(data.item)"
                    >
                      <span class="platform-list-page__actions-item-content">
                        <i
                          class="flaticon2-box-1 platform-list-page__actions-item-icon"
                        ></i>
                        Quản lý sản phẩm
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-item
                      class="platform-list-page__actions-item"
                      @click="manageOrders(data.item)"
                    >
                      <span class="platform-list-page__actions-item-content">
                        <i
                          class="flaticon2-shopping-cart platform-list-page__actions-item-icon"
                        ></i>
                        Quản lý đơn hàng
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-item
                      class="platform-list-page__actions-item"
                      @click="viewDetail(data.item.id)"
                    >
                      <span class="platform-list-page__actions-item-content">
                        <i
                          class="flaticon-eye platform-list-page__actions-item-icon"
                        ></i>
                        Xem chi tiết
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-divider></b-dropdown-divider>
                    <b-dropdown-item
                      class="platform-list-page__actions-item"
                      @click="refreshConnection(data.item)"
                      :disabled="refreshingIds.includes(data.item.id)"
                    >
                      <span class="platform-list-page__actions-item-content">
                        <b-spinner
                          v-if="refreshingIds.includes(data.item.id)"
                          small
                          class="platform-list-page__actions-item-icon"
                        ></b-spinner>
                        <i
                          v-else
                          class="flaticon2-refresh platform-list-page__actions-item-icon"
                        ></i>
                        {{
                          refreshingIds.includes(data.item.id)
                            ? 'Đang làm mới...'
                            : 'Làm mới kết nối'
                        }}
                      </span>
                    </b-dropdown-item>
                    <b-dropdown-divider></b-dropdown-divider>
                    <b-dropdown-item
                      class="platform-list-page__actions-item platform-list-page__actions-item--danger"
                      @click="confirmDisconnect(data.item)"
                    >
                      <span
                        class="platform-list-page__actions-item-content platform-list-page__actions-item-content--danger"
                      >
                        <i
                          class="flaticon2-delete platform-list-page__actions-item-icon"
                        ></i>
                        Ngắt kết nối
                      </span>
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </template>
            </b-table>

            <div
              v-if="loading && connections.length === 0"
              class="text-center py-5"
            >
              <b-spinner variant="primary"></b-spinner>
              <p class="text-muted mt-3">Đang tải danh sách kết nối...</p>
            </div>
          </template>
        </KTCodePreview>
      </b-col>
    </b-row>

    <b-modal
      id="disconnect-modal"
      v-model="showDisconnectModal"
      title="Xác nhận ngắt kết nối"
      @ok="handleDisconnect"
      ok-title="Ngắt kết nối"
      ok-variant="danger"
      cancel-title="Hủy"
    >
      <p v-if="selectedConnection">
        Bạn có chắc chắn muốn ngắt kết nối với
        <strong>{{ selectedConnection.shopName }}</strong> trên
        <strong>{{ getPlatformName(selectedConnection.platform) }}</strong
        >?
      </p>
      <p class="text-muted mb-0">
        <small
          >Hành động này sẽ xóa kết nối và bạn cần thiết lập lại để tiếp tục sử
          dụng.</small
        >
      </p>
    </b-modal>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import Loader from '@/view/content/Loader.vue';
import { getToastInstance } from '@/utils/toastHelper';
import { mapGetters } from 'vuex';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';

const platformRepository = RepositoryFactory.get('platformIntegration');

export default {
  name: 'PlatformListPage',
  components: {
    KTCodePreview,
    Loader,
  },
  data() {
    return {
      connections: [],
      loading: false,
      error: null,
      showDisconnectModal: false,
      selectedConnection: null,
      refreshingIds: [],
      tableFields: [
        { key: 'platform', label: 'Sàn TMĐT', sortable: false },
        { key: 'shopName', label: 'Tên gian hàng', sortable: false },
        { key: 'storeName', label: 'Cửa hàng', sortable: false },
        {
          key: 'installmentAccount',
          label: 'TK trả góp',
          sortable: false,
        },
        { key: 'status', label: 'Trạng thái', sortable: false },
        { key: 'createdAt', label: 'Ngày kết nối', sortable: true },
        { key: 'actions', label: '', class: 'text-right' },
      ],
    };
  },
  computed: {
    ...mapGetters(['layoutConfig']),
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'));
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo');
    },
  },
  async created() {
    this.loadBreadcrumb();
    await this.loadConnections();
  },
  methods: {
    ...getToastInstance(),

    loadBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Kênh bán' },
        { title: 'Danh sách kết nối' },
      ]);
    },

    async loadConnections() {
      try {
        this.loading = true;
        this.error = null;
        const response = await platformRepository.getConfigs();

        this.connections = (response.items || []).map((item) => ({
          ...item,
          storeName: item.store?.name || null,
        }));

        console.log(
          '[PlatformListPage] Loaded connections:',
          this.connections.length,
        );
      } catch (error) {
        console.error('Error loading connections:', error);
        this.error = 'Không thể tải danh sách kết nối. Vui lòng thử lại.';
        this.makeToastFailure('Lỗi khi tải danh sách kết nối');
      } finally {
        this.loading = false;
      }
    },

    navigateToConnect() {
      this.$router.push({ name: 'platform-integration-connect' });
    },

    viewDetail(id) {
      this.$router.push({
        name: 'platform-integration-detail',
        params: { id },
      });
    },

    manageProducts(connection) {
      this.$router.push({
        name: 'platform-integration-products',
        query: { configId: connection.id },
      });
    },

    manageOrders(connection) {
      this.$router.push({
        name: 'platform-integration-orders',
        query: { configId: connection.id },
      });
    },

    async refreshConnection(connection) {
      if (!connection || this.refreshingIds.includes(connection.id)) return;

      try {
        this.refreshingIds.push(connection.id);
        await platformRepository.refreshToken(connection.id);
        this.$bvToast.toast(
          `Đã làm mới kết nối với ${connection.shopName} thành công`,
          {
            title: 'Thành công',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000,
          },
        );
        await this.loadConnections();
      } catch (error) {
        this.makeToastFailure('Không thể làm mới kết nối. Vui lòng thử lại.');
      } finally {
        this.refreshingIds = this.refreshingIds.filter(
          (id) => id !== connection.id,
        );
      }
    },

    confirmDisconnect(connection) {
      this.selectedConnection = connection;
      this.showDisconnectModal = true;
    },

    async handleDisconnect() {
      if (!this.selectedConnection) return;

      try {
        await platformRepository.disconnect(this.selectedConnection.id);
        this.$bvToast.toast(
          `Đã ngắt kết nối với ${this.selectedConnection.shopName} thành công`,
          {
            title: 'Thành công',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000,
          },
        );
        await this.loadConnections();
        this.selectedConnection = null;
      } catch (error) {
        console.error('Error disconnecting platform:', error);
        this.makeToastFailure('Không thể ngắt kết nối. Vui lòng thử lại.');
      }
    },

    getPlatformLogo(platform) {
      const logos = {
        shopee: '/img/platforms/shopee.svg',
        tiktok: '/img/platforms/tiktok.svg',
      };
      return logos[platform] || '/img/platforms/default.svg';
    },

    getPlatformName(platform) {
      const names = {
        shopee: 'Shopee',
        tiktok: 'TikTok Shop',
      };
      return names[platform] || platform;
    },

    getEffectiveStatus(item) {
      if (item.status === 'connected' && item.tokenExpiresAt) {
        const expiresAt = new Date(item.tokenExpiresAt);
        if (expiresAt <= new Date()) {
          return 'expired';
        }
      }
      return item.status;
    },

    getStatusVariant(item) {
      const status = this.getEffectiveStatus(item);
      const variants = {
        connected: 'success',
        expired: 'warning',
        error: 'danger',
      };
      return variants[status] || 'secondary';
    },

    getStatusIcon(item) {
      const status = this.getEffectiveStatus(item);
      const icons = {
        connected: '🟢',
        expired: '🟠',
        error: '🔴',
      };
      return icons[status] || '⚪';
    },

    getStatusText(item) {
      const status = this.getEffectiveStatus(item);
      const statusTexts = {
        connected: 'Đã kết nối',
        expired: 'Hết hạn',
        error: 'Lỗi',
      };
      return statusTexts[status] || status;
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.platform-list-page {
  &__header {
    margin-bottom: 1rem;
  }

  &__logo {
    width: 32px;
    height: 32px;
    object-fit: contain;
  }

  &__table {
    background: white;
    border-radius: 4px;
  }

  &__empty {
    background: white;
    border-radius: 4px;
    padding: 2rem;

    .icon-3x {
      font-size: 3rem;
      display: block;
    }
  }

  &__actions {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__actions-toggle {
    border: none !important;
    background: transparent !important;
    padding: 0.375rem 0.5rem !important;
    border-radius: 0.375rem !important;
    transition: background-color 0.15s ease-in-out;

    &:hover {
      background-color: #f3f6f9 !important;
    }

    &:focus {
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
    }
  }

  &__actions-icon {
    font-size: 1.1rem;
    color: #7e8299;
    transition: color 0.15s ease-in-out;

    &:hover {
      color: #3699ff;
    }
  }

  &__actions-item {
    padding: 0;

    &:hover {
      background-color: #f3f6f9;
    }

    &:focus {
      background-color: #e1e9ff;
      outline: none;
    }

    &--danger:hover {
      background-color: #fff5f8;
    }
  }

  &__actions-item-content {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.625rem 1rem;
    color: #3f4254;
    font-size: 0.925rem;
    font-weight: 500;
    text-decoration: none;
    transition: color 0.15s ease-in-out;

    &:hover {
      color: #181c32;
    }

    &--danger {
      color: #f64e60;

      &:hover {
        color: #ee2d41;
      }
    }
  }

  &__actions-item-icon {
    font-size: 1rem;
    width: 1.25rem;
    text-align: center;
    flex-shrink: 0;
  }

  :deep(.table) {
    td {
      vertical-align: middle;
    }
  }

  :deep(.table-responsive) {
    overflow: visible;
  }

  :deep(.dropdown-menu) {
    z-index: 1050;
    border: 1px solid #ebedf3;
    box-shadow: 0 0 50px 0 rgba(82, 63, 105, 0.15);
    border-radius: 0.42rem;
    padding: 0.5rem 0;
    min-width: 12rem;

    .dropdown-item {
      padding: 0;
      border: none;
      background: none;

      &:hover,
      &:focus {
        background-color: transparent;
      }

      &:active {
        background-color: transparent;
      }
    }

    .dropdown-divider {
      margin: 0.5rem 0;
      border-top-color: #ebedf3;
    }
  }
}
</style>
